import Styles from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/SelectImage.module.scss'
import ModalStyles from './ImagePickerModal.module.scss'
import { Modal } from 'almanac-ui/Modal'
import { ImagePickerProvider, useImagePickerContext } from './ImagePickerContext'
import { Stock } from './Stock/Stock'
import { Link } from './Link/Link'
import { ImagePickerModalContainerProps, ImagePickerModalProps } from './Types'
import { AiGeneration } from './AIGeneration/AiGeneration'
import { SelectedImage } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { StockImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/StockImages'
import { LibraryImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/LibraryImages'
import { Button } from 'blaze-ui/Button'
import { AI, LinkAngled, Upload5 } from 'blaze-ui/icons'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useStateRef } from 'hooks/useStateRef'
import { BrandKitImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/BrandKitImages'
import { AllUploadsContent } from './Uploads/AllUploads'
import { ConfirmButton } from './ConfirmButton'

const ImagePickerModalV2 = ({ title = 'Add or Generate Image', brandKitId, ...props }: ImagePickerModalProps) => {
  const imagePickerContext = useImagePickerContext()
  const { provider, onCancel, setSelectedImages, setProvider, accept, onConfirm, onUploadStart, uploadImages } =
    imagePickerContext
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)

  const onSelectImage = (image: SelectedImage) => {
    setSelectedImages([image])
  }

  const handleOnChange = async (files: File[]) => {
    const toast = showToastMessage('Uploading...', 'loading', undefined, 0)
    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)
    toast()
    onConfirm(uploadedImages, imagePickerContext)
  }

  return (
    <Modal.Root
      size="md"
      onClose={onCancel}
      bodyClassName={ModalStyles.v2Body}
      aria-labelledby="picker-header"
      centered
      {...props}
    >
      <Modal.Header id="picker-header" title={title} />
      <Modal.Content>
        <div className={Styles.selectImageContainer}>
          {provider === 'ai' && <AiGeneration />}
          {provider === 'stock' && <Stock containerClassName={ModalStyles.stock} />}
          {provider === 'link' && <Link containerClassName={ModalStyles.link} />}
          {provider === 'uploads' && <AllUploadsContent containerClassName={ModalStyles.allUploads} />}
          {!provider && (
            <>
              <div className={Styles.sections}>
                <StockImages />
                {brandKitId ? (
                  <BrandKitImages onSelectImage={onSelectImage} brandKitId={brandKitId} />
                ) : (
                  <LibraryImages onSelectImage={onSelectImage} onOpenAll={() => setProvider('uploads')} />
                )}
              </div>
              <div className={Styles.actions}>
                <Button frontIcon={Upload5} variant="secondary" onPress={() => fileInputRef?.click()}>
                  Upload Image
                </Button>
                <Button frontIcon={AI} variant="secondary" onPress={() => setProvider('ai')}>
                  Generate from AI
                </Button>
                <Button frontIcon={LinkAngled} variant="secondary" onPress={() => setProvider('link')}>
                  Get image from a link
                </Button>
              </div>

              <VisuallyHidden>
                <input
                  type="file"
                  accept={accept}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    handleOnChange(files)
                  }}
                  ref={setFileInputRef}
                  multiple={false}
                  name="insert image"
                  id="insert image"
                  data-testid="file-input"
                />
              </VisuallyHidden>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {!provider ? (
            <Modal.FooterButton variant="tertiary" size="lg" onPress={onCancel}>
              Cancel
            </Modal.FooterButton>
          ) : (
            <Modal.FooterButton variant="tertiary" size="lg" onPress={() => setProvider(undefined)}>
              Back
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={ModalStyles.footerActions}>
            <ConfirmButton />
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

const ImagePickerModalContainer = ({ title, brandKitId, onPressOutside, ...props }: ImagePickerModalContainerProps) => {
  return (
    <ImagePickerProvider {...props} maxCount={1}>
      <ImagePickerModalV2 title={title} brandKitId={brandKitId} onPressOutside={onPressOutside} />
    </ImagePickerProvider>
  )
}

export { ImagePickerModalContainer as ImagePickerModalV2 }
